import { useEffect, useRef, useState } from "react";

import {
  Loader,
  Modal,
  ModalBody,
  ModalHeader,
  NotificationType,
} from "@transfr-inc/dashboard-components";
import {
  Button,
  Textarea,
  ToggleButton,
} from "@transfr-inc/dashboard-components/forms";

import { useNotification } from "@hooks/use-notification";
import { services } from "@src/dependencies";
import { useApiRequest } from "@utils/http-client";

// The reserved dial whose body is the JSON array of attached document ids —
// state, not prose, so it renders as the documents section, never a textarea.
const DOCUMENTS_FIELD = "documents";

// The reserved dial whose body is the JSON object of system-wide static
// template variables ({name: verbatim text}) — rendered as the static
// variables section. Values fill {{name}} tokens exactly as written and
// never enter the model's context (ENG-24084).
const STATIC_VARIABLES_FIELD = "static_variables";

// Mirrors the backend's static-variable name rule.
const STATIC_VARIABLE_NAME_RE = /^[a-z][a-z0-9_]{0,63}$/;

// Display order and labels for the template-variables reference list.
const VARIABLE_SOURCES = [
  ["generated", "AI-generated (editable via the prompts below)"],
  ["input", "From the report inputs"],
  ["code", "Computed by code"],
  ["static", "Static (defined below)"],
];

function parseAttachedIds(body) {
  try {
    const ids = JSON.parse(body ?? "[]");
    return Array.isArray(ids) ? ids : [];
  } catch {
    return [];
  }
}

function parseStaticVariables(body) {
  try {
    const variables = JSON.parse(body ?? "{}");
    return variables &&
      typeof variables === "object" &&
      !Array.isArray(variables)
      ? variables
      : {};
  } catch {
    return {};
  }
}

const BYTES_PER_KB = 1024;
const BYTES_PER_MB = BYTES_PER_KB * 1024;

function formatSize(bytes) {
  return bytes >= BYTES_PER_MB
    ? `${(bytes / BYTES_PER_MB).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / BYTES_PER_KB))} KB`;
}

/**
 * The impact-report endpoints return two error-detail shapes: validation
 * 4xxs (documents, static_variables, uploads) carry coded { code, message }
 * objects, while other errors carry the app's usual plain-string detail.
 */
function getErrorMessage(error, fallback) {
  const detail = error?.data?.detail;
  return detail?.message ?? (typeof detail === "string" ? detail : fallback);
}

/**
 * Dev tooling: view and edit the Gemini prompts behind deck generation.
 * Each dial resolves to its active stored version, else the code default;
 * saving stores a new version that the next generation picks up. The
 * documents dial is the on/off set of reference documents injected into the
 * model's context, labeled by filename so prompts can reference them by name.
 */
export function ImpactReportPromptConfigModal({ open, onClose, reportType }) {
  const { impactReportService } = services;
  const { addNotification } = useNotification();

  // field -> edited body; only fields the user touched are tracked
  const [edits, setEdits] = useState({});
  const [savingField, setSavingField] = useState();
  const [uploading, setUploading] = useState(false);
  const [newVariableName, setNewVariableName] = useState("");
  const fileInputRef = useRef();

  const {
    loading,
    response,
    sendRequest: refetch,
  } = useApiRequest(
    () => impactReportService.getPromptConfig(reportType),
    false,
  );

  const { response: documentsResponse, sendRequest: refetchDocuments } =
    useApiRequest(() => impactReportService.listDocuments(), false);

  useEffect(() => {
    if (open) {
      setEdits({});
      setNewVariableName("");
      refetch();
      refetchDocuments();
    }
  }, [open, reportType]);

  const onSave = async (field) => {
    setSavingField(field);
    try {
      await impactReportService.savePrompt(reportType, field, edits[field]);
      setEdits((current) => {
        const rest = { ...current };
        delete rest[field];
        return rest;
      });
      addNotification({
        type: NotificationType.success,
        message: `Prompt "${field}" saved.`,
        ttl: 5000,
      });
      await refetch();
    } catch (error) {
      console.error("[Impact Report] - Failed to save prompt", error);
      addNotification({
        type: NotificationType.error,
        message: getErrorMessage(
          error,
          "We couldn't save the prompt. Please retry.",
        ),
        ttl: 8000,
      });
    } finally {
      setSavingField();
    }
  };

  const documentsDial = response?.prompts?.find(
    (dial) => dial.field === DOCUMENTS_FIELD,
  );
  const staticDial = response?.prompts?.find(
    (dial) => dial.field === STATIC_VARIABLES_FIELD,
  );
  const proseDials = response?.prompts?.filter(
    (dial) =>
      dial.field !== DOCUMENTS_FIELD && dial.field !== STATIC_VARIABLES_FIELD,
  );
  const attachedIds = parseAttachedIds(
    edits[DOCUMENTS_FIELD] ?? documentsDial?.body,
  );
  const staticVariables = parseStaticVariables(
    edits[STATIC_VARIABLES_FIELD] ?? staticDial?.body,
  );
  // Code-owned token names — a new static variable may not shadow one.
  const reservedNames = new Set(
    (response?.templateVariables ?? [])
      .filter((variable) => variable.source !== "static")
      .map((variable) => variable.name),
  );

  const onCopyToken = async (name) => {
    try {
      await navigator.clipboard.writeText(`{{${name}}}`);
      addNotification({
        type: NotificationType.success,
        message: `{{${name}}} copied to clipboard.`,
        ttl: 3000,
      });
    } catch {
      // clipboard unavailable (permissions/insecure context) — non-fatal
    }
  };

  const onChangeStaticVariable = (name, value) => {
    setEdits((current) => ({
      ...current,
      [STATIC_VARIABLES_FIELD]: JSON.stringify({
        ...staticVariables,
        [name]: value,
      }),
    }));
  };

  const onRemoveStaticVariable = (name) => {
    const rest = { ...staticVariables };
    delete rest[name];
    setEdits((current) => ({
      ...current,
      [STATIC_VARIABLES_FIELD]: JSON.stringify(rest),
    }));
  };

  const newNameValid =
    STATIC_VARIABLE_NAME_RE.test(newVariableName) &&
    !reservedNames.has(newVariableName) &&
    staticVariables[newVariableName] === undefined;

  const onAddStaticVariable = () => {
    onChangeStaticVariable(newVariableName, "");
    setNewVariableName("");
  };

  const onToggleDocument = (documentId, enabled) => {
    const ids = enabled
      ? [...attachedIds, documentId].toSorted((a, b) => a - b)
      : attachedIds.filter((id) => id !== documentId);
    setEdits((current) => ({
      ...current,
      [DOCUMENTS_FIELD]: JSON.stringify(ids),
    }));
  };

  const onUploadFile = async ({ target }) => {
    const file = target.files?.[0];
    // reset so re-selecting the same file fires onChange again
    target.value = "";
    if (!file) {
      return;
    }
    setUploading(true);
    try {
      await impactReportService.uploadDocument(file);
      addNotification({
        type: NotificationType.success,
        message: `"${file.name}" uploaded. Toggle it on to attach it.`,
        ttl: 5000,
      });
      await refetchDocuments();
    } catch (error) {
      console.error("[Impact Report] - Failed to upload document", error);
      addNotification({
        type: NotificationType.error,
        message: getErrorMessage(
          error,
          "We couldn't upload the document. Please retry.",
        ),
        ttl: 8000,
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal
      modalClassName="impact-report-prompt-config-modal"
      open={open}
      onClose={onClose}
      uniqueName="impact-report-prompt-config-modal"
    >
      <ModalHeader
        className="blue-icon small"
        icon={["fa-regular", "fa-sliders"]}
        title="Prompt Config"
        uniqueName="Prompt Config"
      />
      <ModalBody>
        <p className="impact-report-prompt-config-hint">
          Dev tooling — edits are stored as new versions and used by the next
          generation for this report type. Fields without a stored version use
          the code default.
        </p>
        {loading && <Loader overlay />}
        {response?.templateVariables?.length > 0 && (
          <div className="impact-report-variables-section">
            <div className="impact-report-prompt-dial-header">
              <span className="impact-report-prompt-dial-name">
                template variables
              </span>
            </div>
            <p className="impact-report-documents-hint">
              Every token the slides template can use — click one to copy it. A
              token in the template that isn&apos;t listed here fails
              generation. AI-generated values come from the prompts below;
              everything else is filled by code and cannot be reworded by the
              model.
            </p>
            {VARIABLE_SOURCES.map(([source, label]) => {
              const names = response.templateVariables
                .filter((variable) => variable.source === source)
                .map((variable) => variable.name);
              if (names.length === 0) {
                return;
              }
              return (
                <div key={source} className="impact-report-variables-group">
                  <span className="impact-report-variables-group-label">
                    {label}
                  </span>
                  <div className="impact-report-variables-chips">
                    {names.map((name) => (
                      <button
                        key={name}
                        type="button"
                        className="impact-report-variable-chip"
                        title="Copy to clipboard"
                        onClick={() => onCopyToken(name)}
                      >
                        {`{{${name}}}`}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {staticDial && (
          <div className="impact-report-static-variables-section">
            <div className="impact-report-prompt-dial-header">
              <span className="impact-report-prompt-dial-name">
                static variables
              </span>
              <span
                className={`impact-report-prompt-dial-badge ${
                  staticDial.isDefault ? "" : "stored"
                }`}
              >
                {staticDial.isDefault
                  ? "default"
                  : `stored v${staticDial.version}`}
              </span>
              <Button
                size="small"
                disabled={
                  edits[STATIC_VARIABLES_FIELD] === undefined || !!savingField
                }
                loader={savingField === STATIC_VARIABLES_FIELD}
                onClick={() => onSave(STATIC_VARIABLES_FIELD)}
              >
                Save
              </Button>
            </div>
            <p className="impact-report-documents-hint">
              System-wide boilerplate rendered verbatim into its{" "}
              <code>{"{{token}}"}</code> wherever the template uses it — the
              model never sees these, so they can&apos;t be reworded. Remember
              to add the token to the slides template; a token without a value
              fails generation.
            </p>
            {Object.entries(staticVariables).map(([name, value]) => (
              <div key={name} className="impact-report-static-variable-row">
                <div className="impact-report-prompt-dial-header">
                  <span className="impact-report-variable-chip">
                    {`{{${name}}}`}
                  </span>
                  <Button
                    size="small"
                    disabled={!!savingField}
                    onClick={() => onRemoveStaticVariable(name)}
                  >
                    Remove
                  </Button>
                </div>
                <Textarea
                  className="impact-report-static-variable-value"
                  value={value}
                  onChange={({ target }) =>
                    onChangeStaticVariable(name, target.value)
                  }
                />
              </div>
            ))}
            <div className="impact-report-static-variable-add">
              <input
                className="impact-report-static-variable-name-input"
                type="text"
                placeholder="new_variable_name"
                value={newVariableName}
                onChange={({ target }) =>
                  setNewVariableName(target.value.trim())
                }
              />
              <Button
                size="small"
                disabled={!newNameValid || !!savingField}
                onClick={onAddStaticVariable}
              >
                Add variable
              </Button>
            </div>
            {newVariableName && !newNameValid && (
              <p className="impact-report-documents-hint">
                Names are lowercase letters, digits, and underscores, must start
                with a letter, and can&apos;t match an existing variable.
              </p>
            )}
          </div>
        )}
        {documentsDial && (
          <div className="impact-report-documents-section">
            <div className="impact-report-prompt-dial-header">
              <span className="impact-report-prompt-dial-name">
                reference documents
              </span>
              <span
                className={`impact-report-prompt-dial-badge ${
                  documentsDial.isDefault ? "" : "stored"
                }`}
              >
                {documentsDial.isDefault
                  ? "default"
                  : `stored v${documentsDial.version}`}
              </span>
              <div className="impact-report-prompt-dial-actions">
                <Button
                  size="small"
                  disabled={uploading}
                  loader={uploading}
                  onClick={() => fileInputRef.current?.click()}
                >
                  Upload…
                </Button>
                <Button
                  size="small"
                  disabled={
                    edits[DOCUMENTS_FIELD] === undefined || !!savingField
                  }
                  loader={savingField === DOCUMENTS_FIELD}
                  onClick={() => onSave(DOCUMENTS_FIELD)}
                >
                  Save
                </Button>
              </div>
            </div>
            <p className="impact-report-documents-hint">
              Toggled-on documents ride along on every generation for this
              report type, labeled by filename — prompts can reference them by
              name (e.g. &quot;pull language from state_standards.txt&quot;).
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.txt,.md"
              hidden
              onChange={onUploadFile}
            />
            {documentsResponse?.documents?.length === 0 && (
              <p className="impact-report-documents-empty">
                No documents in the library yet — upload one to get started.
              </p>
            )}
            {documentsResponse?.documents?.map((doc) => (
              <div key={doc.id} className="impact-report-document-row">
                <ToggleButton
                  id={`impact-report-document-${doc.id}`}
                  value={attachedIds.includes(doc.id)}
                  onChange={(value) => onToggleDocument(doc.id, value)}
                />
                <span className="impact-report-document-name">
                  {doc.filename}
                </span>
                <span className="impact-report-document-meta">
                  {formatSize(doc.sizeBytes)} · uploaded{" "}
                  {new Date(doc.createdAt).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        )}
        {proseDials?.map((dial) => {
          const edited = edits[dial.field] !== undefined;
          return (
            <div key={dial.field} className="impact-report-prompt-dial">
              <div className="impact-report-prompt-dial-header">
                <span className="impact-report-prompt-dial-name">
                  {dial.field}
                </span>
                <span
                  className={`impact-report-prompt-dial-badge ${
                    dial.isDefault ? "" : "stored"
                  }`}
                >
                  {dial.isDefault ? "default" : `stored v${dial.version}`}
                </span>
                <Button
                  size="small"
                  disabled={
                    !edited || !edits[dial.field]?.trim() || !!savingField
                  }
                  loader={savingField === dial.field}
                  onClick={() => onSave(dial.field)}
                >
                  Save
                </Button>
              </div>
              <Textarea
                className="impact-report-prompt-dial-body"
                value={edited ? edits[dial.field] : dial.body}
                onChange={({ target }) =>
                  setEdits((current) => ({
                    ...current,
                    [dial.field]: target.value,
                  }))
                }
              />
            </div>
          );
        })}
      </ModalBody>
    </Modal>
  );
}
